import type { WidgetBranding } from "@ai-guidance/shared";
import { loadWidgetState, saveWidgetState } from "../widgetState";
import type { ChatSurface, MessageRole } from "../chatSurface";
import { Presence } from "../presence";
import { DEFAULT_PRIMARY, lightenHex, paletteFrom, rgba } from "../presence/palette";
import { peekChatSessionId } from "../chatSession";
import { setStickyValue, stickyUrlValue } from "../stickyParam";
import { chromeCopy, commandCopy, uiLanguage, type ChromeCopy } from "../uiText";
import { VOICE_FALLBACK_PARAM, VOICE_PARAM, isVoiceParam } from "../voice/voiceName";
import { stripExpressiveTag } from "../voice/tags";
import { buildBubble, buildCard, buildHitArea, ORBCHAT_CSS, type BubbleParts, type CardParts } from "./card";
import { finishWords, reducedMotion, revealWords, showQuoted, showThinking } from "./reveal";

/**
 * Orb-chatul: aceeași materie de la baza paginii, care se desface în card când
 * are ceva de spus. Livrat prin setarea per-app `branding.orbChat` (dashboard →
 * Widget branding) sau prin flagul de tab `?aig_orb_chat=1` (override de
 * depanare); implicit rămâne panoul clasic (`widget.ts`), neatins.
 *
 * Ce ÎNLOCUIEȘTE: bulina, panoul, firul derulabil, compozitorul cu chenar.
 * Ce NU înlocuiește: nimic din plumbing. Sesiunea, /ask, istoricul din
 * `chatHistory.ts`, cardurile de produs, executorul de acțiuni și vocea sunt
 * exact aceleași - orb-chatul implementează `ChatSurface`, iar `sdk.ts` nu știe
 * pe care dintre cele două vorbește.
 *
 * Trei moduri, ca în prototip:
 *   orb      - strâns; orbul respiră, nimic de citit
 *   showing  - ultima replică, cuvânt cu cuvânt
 *   input    - același contur devine câmp transparent
 *
 * Excepția: CÂT TIMP VOCEA E ACTIVĂ nu există card. Orbul rămâne orb și dansează
 * pe audio (asta e tot spectacolul), iar replica plutește DEASUPRA ansamblului,
 * într-o bulă din aceeași sticlă (`buildBubble`). Morful ar ascunde orbul și
 * waveform-ul exact în minutul în care sunt singurul lucru de văzut - ceea ce
 * chiar s-a întâmplat în testul live al lui Radu (9 aug 2026).
 *
 * Istoricul e LA CERERE (chevronul): implicit vezi doar ultimul schimb. Nu e o
 * economie de spațiu, e definiția obiectului - un fir derulabil permanent l-ar
 * face iar o fereastră de chat. Vine din memoria de aici, nimic nou stocat.
 */

/** Cât aștept înainte să arăt „mă gândesc" - sub atât ar clipi degeaba. */
const THINKING_DELAY_MS = 650;

/** Cât rămâne cardul în `speaking` după ultimul cuvânt, ca orbul să se stingă lin. */
const SPEAKING_TAIL_MS = 600;
/** Cât stă orbul mărit la sosire, înainte să se strângă (tranziția de .6 s din CSS închide cele ~2 s). */
const ARRIVE_HOLD_MS = 1400;

/**
 * „O iau de la capăt", scris în loc de apăsat (decizie de proprietar, 12 aug
 * 2026): butonul de reset a plecat de pe sticlă, iar ștergerea conversației e
 * acum o comandă tastată în câmp.
 *
 * Potrivire EXACTĂ, după `trim` + minuscule: fără prefixe, fără sinonime. Un
 * „/clearance" sau un „clear" e o întrebare adevărată a unui vizitator adevărat
 * și trebuie să ajungă la asistent, nu să-i șteargă firul.
 */
const CLEAR_COMMAND = "/clear";

/**
 * Celelalte două comenzi, din aceeași familie și cu aceleași reguli: se opresc
 * în suprafață, nu ating serverul, nu intră în fir.
 *
 * `/voice`, `/voice-1|2|3` - schimbă vocea agentului, scriind în ACELAȘI loc ca
 * `?voice=` (`stickyParam.ts` + `voiceName.ts`), deci linkul de demo și
 * comanda sunt un singur mecanism. Fără cifră - sau cu una din afara setului -
 * cade pe varianta 2 (decizie de proprietar: comanda nu ghicește asocieri).
 *
 * `/debug` - id-ul sesiunii și vocea curentă, ca un tester să poată spune „am
 * pățit-o în sesiunea X" și s-o găsim în triaj. Nimic altceva din interior.
 *
 * Niciuna nu e anunțată nicăieri (fără listă de ajutor, fără placeholder, fără
 * `aria-label`): sunt unelte interne, nu funcții ale vizitatorului.
 */
const VOICE_COMMAND = "/voice";
/**
 * Aliasul românesc al aceleiași comenzi. Nu un sinonim ghicit, ci greșeala
 * observată (12 aug 2026): `/voce-2` scris în card a plecat spre asistent, care
 * pe un magazin l-a citit drept comandă de produs. Aceleași reguli de potrivire
 * ca `/voice`; slash-ul rămâne obligatoriu, deci „voce" într-o întrebare reală
 * ajunge tot la asistent.
 */
const VOICE_COMMAND_RO = "/voce";
const DEBUG_COMMAND = "/debug";

export class OrbChat implements ChatSurface {
  presence?: Presence;

  private host!: HTMLDivElement;
  private shadow!: ShadowRoot;
  private hit!: HTMLButtonElement;
  private parts!: CardParts;
  private bubble!: BubbleParts;

  private mode: "orb" | "showing" | "input" = "orb";
  private historyOpen = false;
  /**
   * Memoria conversației a suprafeței - EXACT ca firul de bule al panoului
   * clasic: în DOM/memorie, atât. Persistarea rămâne unde a fost mereu
   * (`chatHistory.ts`, chemat din `sdk.ts`), deci istoricul la cerere nu adaugă
   * nicio scriere nouă în browserul vizitatorului (G24 neatins).
   */
  private readonly turns: { role: MessageRole; text: string }[] = [];
  private lastAgentText: string | null = null;
  private thinkingTimer?: ReturnType<typeof setTimeout>;
  private speakingTimer?: ReturnType<typeof setTimeout>;
  /** A doua fixare a derulării bulei, la capătul dezvăluirii - vezi `pinBubbleScroll`. */
  private bubbleScrollTimer?: ReturnType<typeof setTimeout>;
  private voiceActive = false;
  /**
   * Vizitatorul a STRÂNS bula cu orbul (25 aug 2026: orbul comută transcriptul,
   * nu mai oprește conversația). Cât e `true`, conținutul nou continuă să intre
   * în bulă, dar n-o mai aprinde - alegerea lui de a asculta fără text rămâne a
   * lui până apasă orbul din nou sau până se încheie conversația.
   */
  private bubbleHidden = false;
  private busy = false;
  private shiftX = 0;
  /** Abonatul ghidării la schimbările sticlei - vezi `onPanelChange`. */
  private panelChangeListener?: () => void;
  private shiftY = 0;
  private flat = false;

  private askHandler?: (question: string) => void;
  private stopHandler?: () => void;
  private closeHandler?: () => void;
  private openHandler?: () => void;
  /**
   * Cardul era deschis pe pagina precedentă (`widgetState.open`, același bit
   * ca la panoul clasic) și așteaptă prima replică restaurată din istoric ca să
   * se desfacă iar. Feedback Andrei, 21 aug 2026: „după click mă duce pe
   * pagina următoare, dar chatul nu rămâne deschis" - panoul clasic se
   * redeschidea singur, orb-chatul pornea mereu strâns.
   */
  private restoreOpen = false;
  /** Sosirea orbului pe o pagină nouă (vezi `arrive`). */
  private arriveTimer?: ReturnType<typeof setTimeout>;
  private voiceHandler?: () => void;
  private resetHandler?: () => void;

  constructor(
    private readonly appId: string,
    private readonly poweredBy: boolean = true,
    private readonly branding: WidgetBranding = {},
    /**
     * `business.language` - limba în care orb-chatul îi vorbește vizitatorului,
     * de la placeholder până la `aria-label`-uri. Vezi `uiText.ts`.
     */
    private readonly language: string | undefined = undefined,
    /**
     * `data-bottom-offset` de pe tagul de script (px, deja clampat în
     * `index.ts`) - ridică tot ansamblul (orb, waveform, caption, card, ținta
     * de click, bula de voce) deasupra unui element de chrome al paginii
     * (banner, bară fixă) pe care CSS-ul din afara shadow root-ului nu-l poate
     * atinge. Panoul clasic rezolvă asta cu `transform` pe `.aig-root` din
     * snippet-ul merchantului - orb-chatul n-are un `.aig-root` de prins, deci
     * are nevoie de propriul knob.
     */
    private readonly bottomOffset: number = 0,
  ) {}

  /** Textele scrise de noi, în limba appului. Un singur tabel, vezi `uiText.ts`. */
  private get copy(): ChromeCopy {
    return chromeCopy(this.language);
  }

  mount(): void {
    this.host = document.createElement("div");
    // HUD e singura înfățișare a orb-chatului (decizie de proprietar, 19 aug
    // 2026): suprafața închisă de instrument, în care orbul NU se morfează -
    // rămâne orb, iar cardul plutește deasupra lui, ca bula de voce. Fosta
    // sticlă deschisă (și `?aig_skin=` cu ea) a plecat; clasa rămâne fiindcă
    // tot CSS-ul pielii e scoped pe ea (`card.ts`).
    this.host.className = "aig-orbchat-host aig-skin-hud";
    // Aceeași disciplină ca stratul de prezență: gazdă FRATE cu `.aig-root`,
    // direct în body. Vezi `presence/index.ts` - un `transform` pe un strămoș
    // ar face din el containing block pentru `position: fixed`, peste granița
    // shadow DOM, iar „jos-centru" ar deveni „undeva".
    this.host.style.setProperty("pointer-events", "auto", "important");
    // `--aig-lift`: singura sursă a ridicării constante, citită de CSS-ul
    // prezenței ȘI al orb-chatului (`presence/index.ts`, `orbchat/card.ts`) -
    // un singur mecanism, nu unul pe canvas și altul pe DOM. Setat o singură
    // dată, doar când merchantul chiar a cerut-o: absent, host-ul rămâne exact
    // cum era înainte de acest knob (niciun stil inline nou).
    if (this.bottomOffset > 0) this.host.style.setProperty("--aig-lift", `${this.bottomOffset}px`);
    this.shadow = this.host.attachShadow({ mode: "open" });

    // Prezența întâi: ea își pune stilul și canvasurile, iar CSS-ul nostru vine
    // după, ca să poată stinge captionul (în orb-chat cardul E captionul).
    // `alwaysVisible`: aici orbul E conversația, nu decorul de peste ea - deci
    // nimeni (nici sfârșitul unei conversații vocale) n-are voie să-l stingă.
    this.presence = new Presence({
      primaryColor: this.branding.primaryColor,
      alwaysVisible: true,
      hudRing: true,
    });
    this.presence.mount(this.shadow);
    this.flat = !this.presence.isDrawing();

    const palette = paletteFrom(this.branding.primaryColor || DEFAULT_PRIMARY);
    this.host.style.setProperty("--aig-orb-core", palette.core);
    this.host.style.setProperty("--aig-orb-glow", palette.glow);
    this.host.style.setProperty("--aig-orb-deep", palette.deep);
    this.host.style.setProperty("--aig-orb-halo", rgba(palette.glow, 0.28));
    this.host.style.setProperty("--aig-orb-tint", rgba(palette.glow, 0.18));
    // Sticla mată: culoarea clientului amestecată spre alb, aceiași factori ca
    // gradientul din canvas (`drawMorph`), ca varianta plată să fie același
    // obiect, doar nemișcat.
    this.host.style.setProperty("--aig-glass-top", lightenHex(palette.core, 0.93));
    this.host.style.setProperty("--aig-glass-bottom", lightenHex(palette.glow, 0.85));

    const style = document.createElement("style");
    style.setAttribute("data-ai-guidance", "orbchat-css");
    style.textContent = ORBCHAT_CSS;
    this.shadow.appendChild(style);

    this.restoreOpen = loadWidgetState(this.appId).open === true;
    this.hit = buildHitArea(this.flat, this.copy);
    this.hit.addEventListener("click", () => this.onOrbClick());
    // Atins = folosit: sosirea și-a făcut treaba, orbul revine la mărimea lui.
    this.hit.addEventListener("pointerenter", () => this.settle());
    this.hit.addEventListener("pointerdown", () => this.settle());
    this.parts = buildCard(this.flat, this.copy);
    this.wireCard();
    // Bula poartă transcriptul ȘI pastila „Încheie" (25 aug 2026): singura cale
    // explicită de a opri conversația vocală - orbul doar comută bula.
    this.bubble = buildBubble(this.copy);
    this.bubble.stopBtn.addEventListener("click", () => this.voiceHandler?.());

    // Cardul înaintea bulei: `querySelector` în shadow întoarce primul din
    // arbore, iar textul cardului trebuie să rămână primul rezultat.
    this.shadow.append(this.hit, this.parts.card, this.bubble.bubble);
    document.body.appendChild(this.host);

    // Morful nu se atașează deloc: în HUD cardul e o suprafață DOM de sine
    // stătătoare, iar canvasul rămâne orb la nesfârșit.
    this.presence.show();
    this.arrive();

    document.addEventListener("keydown", this.onDocumentKeyDown);
  }

  private wireCard(): void {
    const { message, input, sendBtn, historyBtn, closeBtn, voiceBtn } = this.parts;
    closeBtn.addEventListener("click", () => this.close());
    // Clic pe text → scrii. Nu cât timp gândește: acolo n-ar înlocui o replică,
    // ci ar întrerupe o acțiune în curs.
    message.addEventListener("click", () => {
      if (this.mode === "showing" && !this.thinking) this.setMode("input");
    });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.submit();
      }
    });
    sendBtn.addEventListener("click", () => this.submit());
    historyBtn.addEventListener("click", () => this.toggleHistory());
    voiceBtn.addEventListener("click", () => this.voiceHandler?.());
  }

  private get thinking(): boolean {
    return this.parts.message.querySelector(".aig-think") !== null;
  }

  /* ------------------------------ stare/moduri ------------------------------ */

  private setMode(mode: "orb" | "showing" | "input"): void {
    // Poarta unică a regulii „în voce orbul rămâne orb": oricine ar cere aici
    // altceva (o notiță, un card de produs, un `setBusy`) primește bula, nu
    // morful. O gardă la fiecare apelant ar fi ratat-o pe următoarea.
    if (this.voiceActive && mode !== "orb") return;
    if (mode !== "orb") this.settle();
    this.mode = mode;
    const { card, input } = this.parts;
    card.classList.toggle("aig-orbcard--on", mode !== "orb");
    card.classList.toggle("aig-orbcard--input", mode === "input");
    if (mode === "orb") this.setHistoryOpen(false);
    this.syncChatting();
    // Sticla a apărut sau a plecat - `panelRect` are alt răspuns de-acum.
    this.panelChangeListener?.();
    if (mode === "input") {
      // Focusul după ce cardul a apucat să se desfacă: pe un câmp încă
      // transparent tastatura mobilă ar sări peste o formă care nu există.
      setTimeout(() => input.focus(), 150);
    }
  }

  /**
   * „Se scrie" - cardul deschis pe TEXT ia locul orbului (CSS-ul stinge
   * canvasurile prezenței și ținta de click, iar cardul coboară la muchia
   * de jos). Pe un telefon cu tastatura deschisă, pagina vizibilă e o fâșie, iar
   * orbul cu waveform-ul îi mâncau treimea de jos exact când răspunsul trebuia
   * citit (raport de tester cu captură, 11 aug 2026).
   *
   * O singură poartă, ca `setMode`: starea depinde de modul curent și de voce,
   * și oricare dintre ele se schimbă din mai multe locuri. Vocea o scoate
   * mereu - acolo orbul care dansează E spectacolul, iar bula plutește
   * deasupra lui exact ca înainte.
   */
  private syncChatting(): void {
    if (!this.host) return;
    this.host.classList.toggle("aig-hud-chatting", this.mode !== "orb" && !this.voiceActive);
  }

  private onOrbClick(): void {
    // În voce orbul NU mai încheie conversația (feedback 24 aug 2026: tap-ul
    // accidental o omora) - comută TRANSCRIPTUL (cerere 25 aug 2026): bula se
    // strânge și se re-arată din orb, iar oprirea rămâne doar pastila de pe ea.
    if (this.voiceActive) {
      this.toggleVoiceBubble();
      return;
    }
    // Cât timp răspunsul e pe drum, orbul e butonul de oprire - panoul clasic
    // are un buton dedicat în compozitor, aici compozitorul nici nu e pe ecran.
    // Aceeași semantică: opritul nu trunchiază, doar încheie (vezi `runAsk`).
    if (this.busy) {
      this.stopHandler?.();
      return;
    }
    if (this.mode === "orb") this.open();
    else this.close();
  }

  /**
   * Sosirea: pe o pagină proaspăt încărcată orbul intră la 1,5× și se strânge
   * la mărimea lui după ~2 s dacă nimeni nu-l atinge (feedback Andrei, 21 aug
   * 2026: „semnul de chat nu iese în evidență"). Nu și când cardul revine
   * deschis de pe pagina dinainte - acolo conversația e deja pe ecran - și nu
   * sub `prefers-reduced-motion`. Desenul e în CSS (`card.ts`, `.aig-orb-arrive`).
   */
  private arrive(): void {
    if (!this.host || this.restoreOpen || reducedMotion()) return;
    this.host.classList.add("aig-orb-arrive");
    this.arriveTimer = setTimeout(() => this.settle(), ARRIVE_HOLD_MS);
  }

  private settle(): void {
    clearTimeout(this.arriveTimer);
    this.host?.classList.remove("aig-orb-arrive");
  }

  // NU mai există închidere la click în pagină (decizie 24 aug 2026, după două
  // iterații): întâi „un clic în afară închide" (22 aug), apoi o excepție
  // pentru clickul pe elementul evidențiat de ghidare - dar orice click legitim
  // pe sit (un link, un produs) tot strângea conversația la mijlocul ei. Cardul
  // se închide DOAR printr-un gest explicit: X-ul din colț, orbul, sau Escape.

  private readonly onDocumentKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== "Escape" || this.mode === "orb") return;
    this.close();
  };

  private restoreLastAgent(): void {
    if (this.lastAgentText === null) {
      this.close();
      return;
    }
    // Fără re-animare: replica nu e nouă, doar te-ai întors la ea.
    revealWords(this.parts.message, this.lastAgentText, { instant: true });
    this.setMode("showing");
  }

  private toggleHistory(): void {
    this.setHistoryOpen(!this.historyOpen);
  }

  private setHistoryOpen(open: boolean): void {
    this.historyOpen = open;
    this.parts.card.classList.toggle("aig-orbcard--history", open);
    // Chevronul plutitor din colț ESTE comutatorul: deschis, se întoarce
    // (`--flip`, o rotație în CSS) și arată drumul înapoi. Nu `--live`: fondul
    // colorat e rezervat microfonului deschis, iar aici ar fi cel mai zgomotos
    // lucru de pe sticlă pentru cel mai discret control al ei.
    this.parts.historyBtn.classList.toggle("aig-ibtn--flip", open);
    const label = open ? this.copy.historyHide : this.copy.historyShow;
    this.parts.historyBtn.setAttribute("aria-label", label);
    this.parts.historyBtn.title = label;
    if (!open) {
      this.parts.history.replaceChildren();
      return;
    }
    this.renderHistory();
  }

  /** Din memoria de aici - nimic citit din storage, nimic scris. */
  private renderHistory(): void {
    const nodes: HTMLElement[] = this.turns.map((turn) => {
      const line = document.createElement("div");
      line.className = `aig-orbcard-turn aig-orbcard-turn--${turn.role}`;
      line.textContent = turn.role === "user" ? `„${turn.text}”` : turn.text;
      return line;
    });
    // Piciorul istoricului: doar atribuirea, când planul o cere. Resetul a fost
    // aici până pe 12 aug 2026; acum e comanda scrisă „/clear" (vezi `submit`).
    if (this.poweredBy) {
      const foot = document.createElement("div");
      foot.className = "aig-orbcard-foot";
      const attribution = document.createElement("div");
      attribution.className = "aig-orbcard-poweredby";
      const link = document.createElement("a");
      link.href = "https://askmywebsiteai.com";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = "Powered by AskMyWebsite[AI]";
      attribution.appendChild(link);
      foot.appendChild(attribution);
      nodes.push(foot);
    }
    this.parts.history.replaceChildren(...nodes);
    this.parts.history.scrollTop = this.parts.history.scrollHeight;
  }

  private submit(): void {
    const text = this.parts.input.value.trim();
    if (!text) return;
    this.parts.input.value = "";
    // Comenzile se opresc AICI, înainte de orice: nu pleacă spre server, nu
    // intră în fir, nu apar pe sticlă ca replici. Vezi `runCommand`.
    if (this.runCommand(text.toLowerCase())) return;
    this.askHandler?.(text);
    // Întrebarea a plecat, deci câmpul nu mai are ce primi: pe telefon focusul
    // rămas ținea tastatura deschisă peste răspuns, iar pagina rămânea la
    // jumătate de înălțime. Costul e un clic în plus pe desktop ca să scrii din
    // nou - acceptat de proprietar (11 aug 2026), fiindcă ecranul mic e cazul
    // care pierdea răspunsul.
    this.parts.input.blur();
  }

  /* ---------------------------- comenzi scrise ---------------------------- */

  /**
   * Textul deja `trim`-uit și cu minuscule. `true` = a fost o comandă, deci
   * apelantul se oprește: nimic nu pleacă spre gazdă.
   *
   * Potrivire EXACTĂ, singura excepție fiind prefixele `"/voice-"` / `"/voce-"`
   * (comanda are un argument). Un „/clearance", „clear" sau „/voicex" e o
   * întrebare adevărată a unui vizitator adevărat și trebuie să ajungă la
   * asistent.
   */
  private runCommand(text: string): boolean {
    if (text === CLEAR_COMMAND) {
      this.runClearCommand();
      return true;
    }
    if (text === DEBUG_COMMAND) {
      this.runDebugCommand();
      return true;
    }
    for (const command of [VOICE_COMMAND, VOICE_COMMAND_RO]) {
      if (text === command || text.startsWith(`${command}-`)) {
        this.runVoiceCommand(text.slice(command.length + 1));
        return true;
      }
    }
    return false;
  }

  /** Cifra vocii aleasă acum în tabul ăsta; lipsă = vocea implicită, 1. */
  private get voiceParam(): string {
    return stickyUrlValue(VOICE_PARAM, isVoiceParam) ?? "1";
  }

  private runVoiceCommand(arg: string): void {
    const variant = isVoiceParam(arg) ? arg : VOICE_FALLBACK_PARAM;
    setStickyValue(VOICE_PARAM, variant);
    // O conversație în curs continuă cu vocea VECHE (providerul o are deja
    // deschisă), deci se închide aici - altfel comanda ar părea că n-a făcut
    // nimic. Aceeași cale ca apăsarea butonului: gazda oprește controllerul.
    if (this.voiceActive) this.voiceHandler?.();
    this.showLocalLine(commandCopy(uiLanguage(this.language)).voiceSet.replace("{0}", variant));
  }

  private runDebugCommand(): void {
    // `peek`, nu `ensure`: o comandă de diagnostic n-are voie să CREEZE o
    // sesiune care altfel n-ar fi existat.
    const sessionId = peekChatSessionId(this.appId) ?? "-";
    this.showLocalLine(
      commandCopy(uiLanguage(this.language))
        .debug.replace("{0}", sessionId)
        .replace("{1}", this.voiceParam),
    );
  }

  /**
   * Răspunsul unei comenzi: o linie pe sticlă și atât. Trece prin `addNotice`,
   * adică pe exact drumul notițelor de interfață - nu se salvează nicăieri, nu
   * intră în `turns` (deci nici în istoric, nici în logica salutului) și nu
   * atinge ultima replică a asistentului.
   */
  private showLocalLine(text: string): void {
    this.addNotice(text);
  }

  /* ------------------------------ /clear ------------------------------ */

  /**
   * Ce făcea butonul de reset, fără buton: aceeași singură acțiune ireversibilă
   * a gazdei (`onReset` → `sdk.ts`: oprește vocea, stinge ghidarea, șterge
   * istoricul local și ia un id de sesiune nou), urmată de o reîncărcare a
   * paginii.
   *
   * Reîncărcarea e ce înlocuiește confirmarea de dinainte: comanda e scrisă
   * deliberat, nu apăsată din greșeală, iar pagina proaspătă e cea mai onestă
   * dovadă că firul chiar a plecat - inclusiv pentru ghidarea injectată în
   * pagină, care altfel ar supraviețui în DOM.
   */
  private runClearCommand(): void {
    this.resetHandler?.();
    this.reloadPage();
  }

  /**
   * Învelișul peste `location.reload()` - există ca să poată fi înlocuit în
   * teste (jsdom nu navighează), nu ca abstracție.
   */
  private reloadPage(): void {
    window.location.reload();
  }

  /* ------------------------------ ChatSurface ------------------------------- */

  setHidden(hidden: boolean): void {
    if (!this.host) return;
    this.host.classList.toggle("aig-orbchat-hidden", hidden);
    if (hidden) this.setMode("orb");
  }

  addMessage(role: MessageRole, text: string, opts: { reveal?: boolean } = {}): void {
    // Tagul expresiv se curăță la RANDARE, ca în panoul clasic: istoricul
    // rămâne brut, exact ca textul trimis spre sinteză (vezi voice/tags.ts).
    const shown = stripExpressiveTag(text);
    this.turns.push({ role, text: shown });
    clearTimeout(this.thinkingTimer);
    if (this.voiceActive) {
      // Tura MUTĂ (spațiul-placeholder dinaintea unei navigări) nu e transcript:
      // fără gardă, ar aprinde o sticlă goală peste pagină - fix ce nu are voie
      // să facă un transcript (vezi `setVoiceActive`).
      if (!shown.trim()) return;
      // Nici aici nu se pierde ultima replică: după ce vocea se închide, cardul
      // deschis cu degetul trebuie să arate exact ce s-a spus ultima dată.
      if (role !== "user") this.lastAgentText = shown;
      this.renderInBubble(role, shown, opts.reveal === true);
      return;
    }
    if (role === "user") {
      this.showIfEngaged(() => showQuoted(this.parts.message, shown));
      return;
    }
    this.lastAgentText = shown;
    this.parts.body.replaceChildren();
    // Replica restaurată din istoric (fără reveal) pe o pagină nouă, cu cardul
    // lăsat deschis pe cea dinainte: se desface ca la `restoreLastAgent`, fără
    // să anunțe gazda - nu e o deschidere, e continuarea aceleiași stări.
    if (this.restoreOpen && !opts.reveal && this.mode === "orb") {
      this.restoreOpen = false;
      this.setMode("showing");
    }
    this.showIfEngaged(() => {
      const duration = revealWords(this.parts.message, shown, { instant: !opts.reveal });
      if (!opts.reveal) return;
      this.presence?.setState("speaking");
      clearTimeout(this.speakingTimer);
      this.speakingTimer = setTimeout(() => this.presence?.setState("idle"), duration + SPEAKING_TAIL_MS);
    });
    if (this.historyOpen) this.renderHistory();
  }

  /**
   * Cardul se desface singur DOAR dacă vizitatorul e deja în conversație (l-a
   * deschis, sau vorbește). Salutul de la încărcarea paginii nu are voie să
   * desfacă nimic peste situl clientului - rămâne în memorie și apare la prima
   * atingere a orbului, exact ca deschiderea proactivă din prototip.
   *
   * Vocea nu trece pe aici deloc: are bula ei, iar `addMessage` se ramifică
   * înainte.
   */
  private showIfEngaged(render: () => void): void {
    if (this.mode === "orb") return;
    render();
    if (this.mode !== "showing") this.setMode("showing");
    if (this.historyOpen) this.setHistoryOpen(false);
  }

  /* --------------------------- bula de voce --------------------------- */

  /**
   * Aceeași dezvăluire cuvânt cu cuvânt ca pe card (`reveal.ts`, nimic nou):
   * elementul poartă aceleași clase, deci și aceleași reguli CSS.
   *
   * Ce NU face, spre deosebire de calea cardului: nu atinge starea prezenței.
   * Cât timp vocea e vie, `voice/controller.ts` e cel care conduce orbul din
   * audio real - un `setState("speaking")` de aici i-ar lupta în cadre.
   */
  private renderInBubble(role: MessageRole, shown: string, reveal: boolean): void {
    const { message, body } = this.bubble;
    let revealMs = 0;
    if (role === "user") {
      showQuoted(message, shown);
    } else {
      body.replaceChildren();
      revealMs = revealWords(message, shown, { instant: !reveal });
    }
    this.setBubbleOpen(true);
    this.pinBubbleScroll(revealMs);
  }

  /**
   * Ține transcriptul lipit de capătul de JOS al bulei.
   *
   * Bula are plafon (`CARD_MAX_HEIGHT`) și derulează în interior, deci o replică
   * lungă - sau un rând de carduri adăugat sub ea - împinge CUVINTELE NOI sub
   * muchie, iar vizitatorul rămâne cu începutul unei fraze pe care vocea a
   * terminat-o de mult. Pe scris ar fi doar incomod; pe voce e pierdere curată:
   * nu există scrollback, iar ce nu se vede în clipa în care se aude nu se mai
   * vede niciodată.
   *
   * De două ori, nu o dată: dezvăluirea cuvânt cu cuvânt adaugă conținut timp
   * de câteva secunde DUPĂ ce randarea s-a întors, deci înălțimea crește după
   * prima fixare. `revealWords` întoarce exact durata aia - o folosim, în loc
   * să ghicim un interval sau să punem un observer peste un element care se
   * schimbă la fiecare cuvânt.
   */
  private pinBubbleScroll(revealMs = 0): void {
    const { scroll } = this.bubble;
    scroll.scrollTop = scroll.scrollHeight;
    clearTimeout(this.bubbleScrollTimer);
    if (revealMs <= 0) return;
    this.bubbleScrollTimer = setTimeout(() => {
      scroll.scrollTop = scroll.scrollHeight;
    }, revealMs);
  }

  private setBubbleOpen(open: boolean): void {
    // Bula ascunsă cu orbul rămâne ascunsă: conținutul nou intră în ea (mai
    // jos, apelanții scriu direct în `message`/`body`), dar n-o re-aprinde -
    // altfel fiecare replică ar anula gestul vizitatorului.
    this.bubble.bubble.classList.toggle("aig-orbbubble--on", open && !this.bubbleHidden);
    // Bula a apărut sau a plecat - ghidarea re-decide ferirea (vezi onPanelChange).
    this.panelChangeListener?.();
    if (open) return;
    this.bubbleHidden = false;
    this.bubble.message.replaceChildren();
    this.bubble.body.replaceChildren();
  }

  /**
   * Comutatorul orbului în timpul vocii (25 aug 2026): ASCUNDE sau re-arată
   * transcriptul, fără să-l golească - la re-arătare, ultima replică e tot
   * acolo. Nu trece prin `setBubbleOpen`: acela e ciclul de viață al bulei
   * (voce pornită/încheiată) și golește conținutul la închidere.
   */
  private toggleVoiceBubble(): void {
    this.bubbleHidden = !this.bubbleHidden;
    this.bubble.bubble.classList.toggle("aig-orbbubble--on", !this.bubbleHidden);
    this.panelChangeListener?.();
    if (!this.bubbleHidden) this.pinBubbleScroll();
  }

  /** Aceeași garanție ca pe widget: notița mascării, mecanic, pe sticlă. */
  noticeSensitiveRedacted(): void {
    this.addNotice(this.copy.sensitiveRedacted);
  }

  addNotice(text: string): void {
    const note = document.createElement("div");
    note.className = "aig-orbcard-note";
    note.setAttribute("role", "status");
    note.textContent = text;
    if (this.voiceActive) {
      this.bubble.body.replaceChildren(note);
      this.setBubbleOpen(true);
      this.pinBubbleScroll();
      return;
    }
    this.parts.body.replaceChildren(note);
    if (this.mode === "orb") this.setMode("showing");
  }

  addCardsToThread(node: HTMLElement): void {
    // În voce, aceleași carduri intră în bulă: e aceeași sticlă, doar plutind
    // deasupra orbului care încă vorbește.
    if (this.voiceActive) {
      this.bubble.body.appendChild(node);
      this.setBubbleOpen(true);
      // Cardurile intră SUB text și cresc înălțimea: fără fixare, ultimele
      // cuvinte rostite ies din cadru exact când apare produsul despre care sunt.
      this.pinBubbleScroll();
      return;
    }
    // ÎN card, sub text: conținutul împinge înălțimea până la plafon, apoi
    // derulează în interiorul sticlei. Markup-ul cardurilor rămâne al lui
    // `commerce/cards.ts` - aici se schimbă doar containerul (vezi ORBCHAT_CSS).
    this.parts.body.appendChild(node);
    if (this.mode === "orb") this.setMode("showing");
    if (this.historyOpen) this.setHistoryOpen(false);
  }

  addStyle(css: string): void {
    const style = document.createElement("style");
    style.setAttribute("data-ai-guidance", "commerce-css");
    style.textContent = css;
    this.shadow.appendChild(style);
  }

  mountOverlay(node: HTMLElement): void {
    this.shadow.appendChild(node);
  }

  setBusy(busy: boolean): void {
    this.busy = busy;
    clearTimeout(this.thinkingTimer);
    // În voce, starea orbului e a controllerului vocal (o conduce din audio) și
    // nu există card în care să scriem „mă gândesc".
    if (this.voiceActive) return;
    this.presence?.setState(busy ? "processing" : "idle");
    if (!busy) {
      if (this.thinking && this.lastAgentText !== null) this.restoreLastAgent();
      return;
    }
    if (this.mode === "orb") this.setMode("showing");
    // Întâi rămâne întrebarea citată; abia dacă așteptarea se lungește, gândirea
    // devine ea acțiunea de pe ecran.
    this.thinkingTimer = setTimeout(
      () => showThinking(this.parts.message, this.copy.thinkingLabel),
      reducedMotion() ? 0 : THINKING_DELAY_MS,
    );
  }

  finishReveal(): void {
    finishWords(this.parts.message);
    clearTimeout(this.speakingTimer);
    this.presence?.setState("idle");
  }

  isOpen(): boolean {
    return this.mode !== "orb";
  }

  /**
   * Deschis = ultima replică PLUS câmpul de scris, dintr-un singur gest.
   *
   * Era în doi timpi (atingi orbul, citești, atingi textul ca să poți scrie):
   * corect ca poveste - obiectul „vorbește", apoi „ascultă" - dar în practică
   * vizitatorul care deschidea widgetul ca să întrebe ceva trebuia să ghicească
   * un al doilea gest ca să ajungă la tastatură. Replica rămâne pe ecran, deci
   * nu se pierde nimic din ce era; se pierde doar pasul.
   */
  open(): void {
    // Fără re-animare: replica nu e nouă, doar te-ai întors la ea.
    if (this.lastAgentText !== null) {
      revealWords(this.parts.message, this.lastAgentText, { instant: true });
    }
    this.setMode("input");
    // Același storage și aceeași cheie ca panoul clasic (widget.ts), deci
    // handoff-ul cross-subdomeniu din sdk.ts o cară deja fără nimic în plus.
    saveWidgetState(this.appId, { open: true });
    this.openHandler?.();
  }

  close(): void {
    clearTimeout(this.thinkingTimer);
    this.setMode("orb");
    saveWidgetState(this.appId, { open: false });
    this.presence?.setState("idle");
    this.closeHandler?.();
  }

  panelRect(): { left: number; top: number; right: number; bottom: number } | null {
    // Strâns, orbul e prea mic ca să stea în calea a ceva - și n-ar avea cine
    // citi replica dacă am muta pagina pentru el. În voce însă obstacolul real
    // e bula, nu cardul: ea e ce acoperă pagina cât timp ghidarea caută loc.
    const glass = this.voiceActive ? this.bubble.bubble : this.parts.card;
    if (glass === this.parts.card && this.mode === "orb") return null;
    // Bula închisă e invizibilă (opacity 0), dar tot are cutie în layout - fără
    // verificarea asta ghidarea s-ar feri de o sticlă pe care nimeni n-o vede.
    if (glass === this.bubble.bubble && !glass.classList.contains("aig-orbbubble--on")) return null;
    const rect = glass.getBoundingClientRect();
    return {
      left: rect.left - this.shiftX,
      top: rect.top - this.shiftY,
      right: rect.right - this.shiftX,
      bottom: rect.bottom - this.shiftY,
    };
  }

  shiftPanel(dx: number, dy: number): void {
    if (dx === this.shiftX && dy === this.shiftY) return;
    this.shiftX = dx;
    this.shiftY = dy;
    // Cardul ȘI sticla de sub el: sunt același obiect, deci se mută împreună.
    // Cardul e centrat cu `translateX(-50%)`, ca bula - deci deplasarea se
    // COMPUNE cu centrarea, exact ca mai jos; un `translate(dx,dy)` simplu
    // l-ar arunca cu jumătate de lățime la dreapta.
    this.parts.card.style.transform =
      dx === 0 && dy === 0 ? "" : `translate(calc(-50% + ${dx}px), ${dy}px)`;
    // Bula e centrată cu `translateX(-50%)`, deci deplasarea se COMPUNE cu
    // centrarea; un `translate(dx, dy)` simplu ar arunca-o cu jumătate de
    // lățime la dreapta.
    this.bubble.bubble.style.transform =
      dx === 0 && dy === 0 ? "" : `translate(calc(-50% + ${dx}px), ${dy}px)`;
    this.presence?.setOffset(dx, dy);
  }

  /**
   * Ghidarea se abonează la schimbările sticlei: bula vocală CREȘTE pe măsură
   * ce replica curge în ea, deci ferirea decisă la aprinderea inelului expira
   * în secunde (12 aug 2026, staging: inelul desenat peste textul bulei).
   * ResizeObserver prinde creșterea; apelurile explicite din setMode /
   * setVoiceActive prind aparițiile și dispariile, pe care observatorul unei
   * cutii deja montate nu le vede întotdeauna la timp.
   */
  onPanelChange(listener: () => void): void {
    this.panelChangeListener = listener;
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => this.panelChangeListener?.());
      observer.observe(this.parts.card);
      observer.observe(this.bubble.bubble);
    }
  }

  onAsk(handler: (question: string) => void): void {
    this.askHandler = handler;
  }

  onStop(handler: () => void): void {
    this.stopHandler = handler;
  }

  onClose(handler: () => void): void {
    this.closeHandler = handler;
  }

  onOpen(handler: () => void): void {
    this.openHandler = handler;
  }

  onVoiceToggle(handler: () => void): void {
    this.voiceHandler = handler;
    // Abia acum butonul are ce face - vezi `buildCard`.
    this.parts.voiceBtn.hidden = false;
  }

  setVoiceActive(active: boolean): void {
    this.voiceActive = active;
    this.parts.voiceBtn.classList.toggle("aig-ibtn--live", active);
    this.parts.voiceBtn.setAttribute(
      "aria-label",
      active ? this.copy.voiceEnd : this.copy.voiceStart,
    );
    // Pastila din pielea `hud` poartă textul la vedere, deci și el comută - nu
    // doar aria-label-ul (feedback 11 aug 2026: butonul de voce trebuie să fie
    // evident, iar o pastilă care minte despre starea ei ar fi mai rea decât una
    // ascunsă).
    this.parts.voiceLabel.textContent = active ? this.copy.voiceEndShort : this.copy.voiceStartShort;
    if (active) {
      // Cardul deschis în text se topește ÎNAINTE ca bula să apară: două sticle
      // deodată ar fi două conversații. `setMode` e chemat cât `voiceActive` e
      // deja `true`, deci trece doar pentru „orb" - exact ce vrem.
      clearTimeout(this.thinkingTimer);
      clearTimeout(this.speakingTimer);
      this.setMode("orb");
      // `setMode` a chemat deja `syncChatting`, dar cu `voiceActive` deja `true`
      // - deci clasa e jos și orbul se vede, exact cum trebuie în voce.
      // Bula se aprinde DIN PRIMA secundă (25 aug 2026, răsturnând decizia din
      // 11 aug de a o lăsa pe primul transcript): acum poartă pastila „Încheie",
      // deci nu mai e o sticlă goală - e locul din care se oprește conversația,
      // și trebuie să existe înainte de primul cuvânt.
      this.bubbleHidden = false;
      this.setBubbleOpen(true);
      return;
    }
    // Vocea s-a încheiat: bula se stinge, iar suprafața redevine ce era -
    // orbul se morfează la atingere, exact ca înainte.
    this.setBubbleOpen(false);
    this.syncChatting();
  }

  /**
   * „O iau de la capăt" - fără buton pe sticlă de pe 12 aug 2026. Handlerul e
   * același al gazdei (`sdk.ts`); ce l-a înlocuit e comanda scrisă „/clear"
   * (vezi `runClearCommand`).
   */
  onReset(handler: () => void): void {
    this.resetHandler = handler;
  }

  /**
   * Firul, gol, cu salutul din nou - adică EXACT starea de dinaintea primei
   * întrebări: salutul așteaptă în memorie, cardul se strânge în orb. Storage-ul
   * nu se atinge de aici (vezi `sdk.ts`).
   */
  resetThread(greeting: string): void {
    clearTimeout(this.thinkingTimer);
    clearTimeout(this.speakingTimer);
    this.turns.length = 0;
    this.lastAgentText = null;
    this.parts.body.replaceChildren();
    this.parts.message.replaceChildren();
    this.parts.input.value = "";
    // Bula ține tot ce s-a spus prin voce: un fir șters n-are voie să lase
    // ultima replică plutind deasupra orbului.
    this.setBubbleOpen(false);
    this.setHistoryOpen(false);
    this.presence?.setState("idle");
    if (greeting) {
      this.turns.push({ role: "agent", text: greeting });
      this.lastAgentText = greeting;
      revealWords(this.parts.message, greeting, { instant: true });
    }
    this.setMode(greeting ? "showing" : "orb");
  }

  /** Doar pentru teste/demontare: SDK-ul nu demontează suprafața în viața reală. */
  destroy(): void {
    document.removeEventListener("keydown", this.onDocumentKeyDown);
    clearTimeout(this.thinkingTimer);
    clearTimeout(this.speakingTimer);
    clearTimeout(this.bubbleScrollTimer);
    clearTimeout(this.arriveTimer);
    this.presence?.destroy();
    this.host?.remove();
  }
}
